import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { AuthShell } from '../components/AuthShell.jsx';

/**
 * Check-your-email page.
 *
 * Shown after a successful POST /api/signup AND successful card capture.
 * The verify email has been fired as part of the signup call (the backend
 * uses waitUntil so it may land milliseconds after the response).
 *
 * Features:
 *  - Reads email from ?e= query param (signup page passes it)
 *  - Resend button, disabled for 30s after page load, re-disabled for 30s
 *    after each successful resend
 *  - Explicit note about checking spam and about dev-mode console logging
 *
 * UX pitfalls avoided:
 *  - We don't auto-poll /api/me to detect verification. The user will
 *    navigate via the email link, which takes them to /verify?token=...
 *    That page then calls POST /api/signup/verify, receives the session
 *    cookie, and navigates to /dashboard. Clean, linear, no magic.
 */

const RESEND_COOLDOWN_SECONDS = 30;

export function SignupPendingPage() {
  const [params] = useSearchParams();
  const email = params.get('e') || '';

  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleResend() {
    if (!email) {
      setResendError('No email on file to resend to. Please refresh the page or start again.');
      return;
    }

    setResending(true);
    setResendMessage(null);
    setResendError(null);

    try {
      await api.post('/api/signup/resend', { email });
      setResendMessage(`A new verification email is on its way to ${email}.`);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      if (e instanceof ApiError) {
        setResendError(e.message);
      } else {
        setResendError('Could not resend. Please try again in a moment.');
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <div
          aria-hidden="true"
          style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--tocyn-green-l)',
            color: 'var(--tocyn-green-d)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontFamily: 'var(--font-display)',
            marginBottom: 'var(--sp-5)',
          }}
        >
          ✓
        </div>

        <h1 className="auth-heading">Check your email.</h1>
        <p className="auth-subheading">
          We've sent a verification link to <strong>{email || 'your inbox'}</strong>.
          Click it and you're in.
        </p>

        <div className="alert alert-info" style={{ marginBottom: 'var(--sp-6)' }}>
          <strong style={{ display: 'block', marginBottom: 'var(--sp-1)' }}>Can't find it?</strong>
          Check your spam or promotions folder. The email is from
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}> noreply@tocynid.com</code>
          {' '}with the subject "Verify your TocynID account".
        </div>

        {resendMessage && (
          <div className="alert alert-success" role="status">{resendMessage}</div>
        )}
        {resendError && (
          <div className="alert alert-error" role="alert">{resendError}</div>
        )}

        <button
          type="button"
          className="btn btn-secondary btn-block"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
        >
          {resending
            ? 'Resending…'
            : cooldown > 0
              ? `Resend in ${cooldown}s`
              : 'Resend verification email'}
        </button>

        <p className="muted text-center" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-5)' }}>
          Wrong email address?{' '}
          <Link to="/signup">Sign up again</Link>
        </p>

        {import.meta.env.DEV && (
          <div
            style={{
              marginTop: 'var(--sp-6)',
              padding: 'var(--sp-4)',
              background: 'var(--cream)',
              border: '1px dashed var(--line)',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--ink-mute)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            <strong>Dev mode:</strong> The verify email isn't sent for real — it's
            logged to the worker console. Run <code>npx wrangler tail --env dev</code> in
            the API repo to see the <code>verifyUrl</code>, then paste it into
            your browser.
          </div>
        )}
      </div>
    </AuthShell>
  );
}
